import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Response, Pagination } from '@ui/src';
import {
  RiskScoreItem,
  ClientsActivity,
  ClientActivitiesValidators,
} from '../../../types';
import { individualClientsRiskApi } from '../services';
import ROUTES from '../../../../routes';

export interface IndividualClientsActivitiesListState {
  values?: ClientsActivity[];
  pagination?: Pagination;
  selectedList: string[];
  validators?: ClientActivitiesValidators;
  errorsList?: string;
}

const initialState: IndividualClientsActivitiesListState = {
  values: undefined,
  selectedList: [],
};

export const individualClientsActivitiesSlice = createSlice({
  name: 'individualClientsActivities',
  initialState,
  reducers: {
    selectList: (state, action: PayloadAction<string[]>) => {
      state.selectedList = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      individualClientsRiskApi.endpoints.getClientsActivitiesValidators
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ClientActivitiesValidators }>) => {
        state.validators = action.payload.data;
      },
    );
    builder.addMatcher(
      individualClientsRiskApi.endpoints.getClientsActivitiesList.matchPending,
      (state) => {
        state.errorsList = undefined;
      },
    );
    builder.addMatcher(
      individualClientsRiskApi.endpoints.getClientsActivitiesList.matchRejected,
      (state, action: any) => {
        state.values = undefined;
        state.pagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.errorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.errorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      individualClientsRiskApi.endpoints.getClientsActivitiesList
        .matchFulfilled,
      (state, action: PayloadAction<Response<RiskScoreItem>>) => {
        state.values = action.payload.data.edges
          .map((item) => ({
            id: item.node.id,
            riskScore: item.node.riskScore,
            name: item.node.name,
            scoreAction: item.node.riskScoreRange.action,
            riskCategory: item.node.riskScoreRange.name,
            riskScoreRangeId: item.node.riskScoreRangeId,
            url: generatePath(ROUTES.INDIVIDUAL_CLIENTS_RISK_ACTIVITIES_EDIT, {
              activityId: item.node.id,
            }),
          }))
          .sort((a, b) => b.riskScore - a.riskScore);

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      individualClientsRiskApi.endpoints.updateClientActivity.matchFulfilled,
      (state, action: PayloadAction<{ data: RiskScoreItem }>) => {
        const { data } = action.payload;

        const nextList = state.values?.map((item) => {
          let result = { ...item };

          if (result.id === data.id) {
            result = {
              id: data.id,
              riskScore: data.riskScore,
              name: data.name,
              scoreAction: data.riskScoreRange.action,
              riskCategory: data.riskScoreRange.name,
              riskScoreRangeId: data.riskScoreRangeId,
              url: generatePath(
                ROUTES.INDIVIDUAL_CLIENTS_RISK_ACTIVITIES_EDIT,
                {
                  activityId: data.id,
                },
              ),
            };
          }

          return result;
        });

        state.values = nextList;
      },
    );
  },
});

export const { selectList } = individualClientsActivitiesSlice.actions;

export default individualClientsActivitiesSlice.reducer;
